<script setup lang="tsx">
import type { TextProps } from './type'

import { CopyOutlined } from '@ant-design/icons-vue'
import { Tooltip } from 'ant-design-vue'
import copy from 'copy-to-clipboard'

import { sleep } from '#/utils'

import { onCopy as onCopyGlobal } from './onCopy'

const props = withDefaults(defineProps<TextProps>(), {
  content: '',
  disabled: false,
  lineClamp: 1,
  color: undefined,
  href: undefined,
  target: '_blank',
  copyable: false,
  onCopy: ((content) => onCopyGlobal?.(content)) as TextProps['onCopy'],
  defaultContent: '-',
  lazy: true,
  block: false,
  label: undefined,
})
const slots = useSlots()

const spanRef = ref<HTMLSpanElement | null>(null)
const isOverflow = ref(false)
const hovered = ref(false)

const lineClampDisabled = computed(() => {
  return props.lineClamp === false
})

const showTooltip = computed(() => {
  const { lazy } = props
  return (
    !lineClampDisabled.value &&
    (lazy ? hovered.value : true) &&
    isOverflow.value
  )
})

const RenderSpan = () => {
  const {
    href,
    target,
    copyable,
    content,
    color,
    defaultContent,
    block,
    label,
    lineClamp,
    labelClassName,
    contentClassName,
  } = props
  const Tag = href ? 'a' : 'span'
  const attrs: Recordable = href ? { href, target, class: `!underline ` } : {}
  const spanClassName = `max-w-full align-middle line-clamp-${lineClamp} break-all ${contentClassName} ${href ? 'text-primary' : ''}`

  const realContent = slots.default?.() ?? content

  return (
    <div
      class={`items-center justify-center whitespace-normal ${block ? 'flex' : 'inline-flex'}`}
      onMouseenter={onMouseEnter}
    >
      <span class={spanClassName} ref={spanRef} style={{ color }}>
        {label && (
          <span class={`text-[var(--antd-color-text-label)] ${labelClassName}`}>
            {label}
          </span>
        )}
        {copyable && realContent && (
          <CopyOutlined
            class="hover:text-primary text-foreground mr-[2px] cursor-pointer"
            onClick={onClick}
          />
        )}
        <Tag {...attrs}>{realContent || defaultContent}</Tag>
      </span>
    </div>
  )
}

function onClick(e: MouseEvent) {
  e.preventDefault()
  const { content, onCopy } = props
  if (copy(content.toString())) {
    onCopy(content)
  }
}

async function checkOverflow() {
  await sleep(50)
  const el = spanRef.value
  if (el) {
    isOverflow.value = el.scrollHeight > el.clientHeight
  }
}

function onMouseEnter() {
  hovered.value = true
}

watch(
  () => props.content,
  () => checkOverflow(),
)

onMounted(checkOverflow)
</script>

<template>
  <Tooltip
    v-if="showTooltip"
    :title="props.content"
    :color="props.color"
    destroy-tooltip-on-hide
  >
    <RenderSpan />
  </Tooltip>
  <RenderSpan v-else />
</template>
